import { el, saveBlob, toast } from './dom';
import { ApiError } from '../core/network/api';
import { COUNTRIES } from '../core/payments/countries';
import { gicon } from './ui/icons';
import { loadAppearance } from './character/appearance-store';
import { composeAvatar } from './character/compositor';
import {
  accountApi,
  type AccountProfile,
  type TransactionEntry,
  type TransactionPage,
} from './game-api';
import { getPaymentMethods } from '../core/payments/payments-api';

/**
 * The "real" half of the settings area: profile, security (password change),
 * account information, and the in-game transaction ledger with PDF export.
 *
 * Each `render*` function owns one settings tab and is handed an empty body
 * element. They all fetch on mount and render straight into it — no shared
 * state, so switching tabs is always a clean read. Everything is laid out to
 * work at phone width: two-column grids collapse to one, and the ledger renders
 * as stacked cards rather than a table that would need horizontal scrolling.
 */

const PAGE = 10;

// ─── shared bits ─────────────────────────────────────────────────────────────

const CARD = 'rounded-xl border border-border bg-surface-2/60 p-3.5';
const LABEL = 'text-[11px] uppercase tracking-wide text-text-dim';
const INPUT = 'w-full bg-surface-2 border border-border rounded px-2.5 py-2 text-sm outline-none focus:border-gold';
const BTN = 'px-3 py-2 rounded border border-border text-sm hover:border-gold hover:text-gold transition disabled:opacity-50 disabled:hover:border-border disabled:hover:text-text';
const BTN_GOLD = 'px-3 py-2 rounded bg-gold text-bg font-bold text-sm hover:brightness-105 active:scale-95 transition disabled:opacity-50';

function spinner(text = 'Loading…') {
  return el('div', 'py-8 text-center text-sm text-text-dim', {}, text);
}

function errorBox(msg: string) {
  return el('div', 'py-6 text-center text-sm text-error', {}, msg);
}

function field(label: string, value: string, cls = '') {
  return el('div', 'min-w-0', {},
    el('div', LABEL, {}, label),
    el('div', `text-sm break-words ${cls}`, {}, value));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
function humanType(t: string): string {
  return t.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}
function humanDescription(d: string): string {
  if (!d) return d;
  return d.includes('_') ? humanType(d) : d;
}
function signed(n: number): string {
  return `${n > 0 ? '+' : ''}${n.toLocaleString()}`;
}

/**
 * Turn a failed PDF download into a sentence a player can act on.
 *
 * These two endpoints are requested with `responseType: 'blob'`, so axios hands
 * the ERROR body over as a Blob too — the interceptor reads `data.message` off
 * an object and finds nothing, and ApiError falls back to "Request failed with
 * status code 400". Every legitimate refusal (an in-game row has no invoice, the
 * ledger is empty) therefore reached the player as a generic shrug. Decoding is
 * done here rather than in the interceptor to keep the blast radius to the two
 * callers that actually ask for blobs.
 */
async function pdfErrorMessage(err: unknown, fallback: string): Promise<string> {
  // 5xx bodies are unhandled exceptions and validation dumps — internal wording
  // that players should never read. Only the curated 4xx refusals get decoded.
  if (err instanceof ApiError && err.status < 500 && err.details instanceof Blob) {
    try {
      const parsed = JSON.parse(await blobText(err.details)) as { message?: string | string[] };
      const m = Array.isArray(parsed.message) ? parsed.message[0] : parsed.message;
      if (m) return m;
    } catch { /* not JSON — fall through to the generic line */ }
  }
  return fallback;
}

/**
 * `Blob.text()` is missing on iOS Safari before 14 (and in jsdom), and this game
 * is played on mobile web — so the FileReader route is a real fallback, not
 * belt-and-braces. Without it the decode above throws and every refusal degrades
 * back to the generic line on exactly the browsers we care about.
 */
function blobText(b: Blob): Promise<string> {
  if (typeof b.text === 'function') return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

// ─── PROFILE ─────────────────────────────────────────────────────────────────

export function renderProfile(body: HTMLElement, userKey: string) {
  body.replaceChildren(spinner());
  accountApi.profile().then((p) => {
    body.replaceChildren(profileView(p, userKey));
  }).catch(() => body.replaceChildren(errorBox('Could not load your profile.')));
}

function profileView(p: AccountProfile, userKey: string): HTMLElement {
  // Hero look, if the character creator has stored one for this player. It lives
  // client-side (appearance-store), so it is composed here rather than fetched.
  const avatar = el('div', 'w-16 h-16 shrink-0 rounded-xl border border-border bg-surface-2 grid place-items-center overflow-hidden');
  avatar.appendChild(gicon('leaf', 26));
  const look = (() => { try { return loadAppearance(userKey); } catch { return null; } })();
  if (look) {
    // Cosmetic only, and async — never block the panel on the sprite sheets.
    composeAvatar(look).then((canvas) => {
      canvas.style.cssText = 'height:56px;width:auto;image-rendering:pixelated';
      avatar.replaceChildren(canvas);
    }).catch(() => { /* keep the fallback icon */ });
  }

  const xpPct = Math.max(0, Math.min(100, Math.round((p.xpIntoLevel / p.xpPerLevel) * 100)));
  const xpBar = el('div', 'h-2 rounded-full bg-surface-2 border border-border overflow-hidden mt-1', {},
    el('div', 'h-full bg-gold', { style: `width:${xpPct}%` }));

  return el('div', 'space-y-3', {},
    el('div', `${CARD} flex items-center gap-3.5`, {},
      avatar,
      el('div', 'min-w-0 flex-1', {},
        el('div', 'text-base font-heading text-gold truncate', {}, p.username ?? p.email),
        el('div', 'text-[12px] text-text-dim truncate', {}, p.email),
        el('div', 'text-[11px] text-text-dim mt-0.5', {}, `Member since ${fmtDate(p.memberSince)}`))),

    el('div', CARD, {},
      el('div', 'flex items-baseline justify-between', {},
        el('div', 'text-sm', {}, `Level ${p.level}`),
        el('div', 'text-[11px] text-text-dim font-mono', {}, `${p.xpIntoLevel} / ${p.xpPerLevel} XP`)),
      xpBar,
      el('div', 'text-[11px] text-text-dim mt-1.5', {}, `${p.xpToNextLevel} XP to level ${p.level + 1} · ${p.xp.toLocaleString()} XP total`)),

    personalDetailsCard(p),

    el('div', `${CARD} grid grid-cols-2 gap-3`, {},
      field('Coins', p.currencies.coins.toLocaleString(), 'text-coin font-mono'),
      field('Shrooms', p.currencies.shrooms.toLocaleString(), 'text-shroom font-mono'),
      field('Rank points', p.rankPoints.toLocaleString(), 'font-mono'),
      field('Email status', p.emailVerified ? 'Verified' : 'Not verified',
        p.emailVerified ? 'text-leaf' : 'text-text-dim')),
  );
}

// ─── ACCOUNT INFORMATION ─────────────────────────────────────────────────────

export function renderAccountInfo(body: HTMLElement) {
  body.replaceChildren(spinner());
  // Withdrawals closed (or unknown) → the "Lifetime withdrawn" row is not shown:
  // it names a feature the player cannot use.
  const withdrawals = getPaymentMethods().then((pm) => pm.withdrawals.enabled).catch(() => false);
  Promise.all([accountApi.profile(), withdrawals]).then(([p, withdrawalsOpen]) => {
    const rows: HTMLElement[] = [
      field('Player ID', p.id, 'font-mono text-[11px]'),
      field('Role', humanType(p.role)),
      field('Username', p.username ?? '—'),
      field('Email', p.email, 'text-[12px]'),
      field('Member since', fmtDate(p.memberSince)),
      field('Last seen', fmtDateTime(p.lastSeenAt)),
    ];

    const wallet = el('div', `${CARD} grid grid-cols-2 gap-3`, {},
      field('Coins', p.currencies.coins.toLocaleString(), 'text-coin font-mono'),
      field('Shrooms', p.currencies.shrooms.toLocaleString(), 'text-shroom font-mono'),
      field('Refundable Shrooms', p.currencies.refundableShrooms.toLocaleString(), 'font-mono'),
      field('Lifetime deposited', p.currencies.lifetimeDeposited.toLocaleString(), 'font-mono'),
      withdrawalsOpen ? field('Lifetime withdrawn', p.currencies.lifetimeWithdrawn.toLocaleString(), 'font-mono') : null,
      field('Level / XP', `${p.level} · ${p.xp.toLocaleString()} XP`, 'font-mono'));

    const farm = p.farm
      ? el('div', `${CARD} grid grid-cols-3 gap-3`, {},
        field('Wood', String(p.farm.wood), 'font-mono'),
        field('Ore', String(p.farm.ore), 'font-mono'),
        field('Gems', String(p.farm.gem), 'font-mono'),
        field('Stone', String(p.farm.stone), 'font-mono'),
        field('Mushrooms', String(p.farm.mushroom), 'font-mono'),
        field('Energy', `${p.farm.energy}%`, 'font-mono'))
      : el('div', `${CARD} text-sm text-text-dim`, {}, 'No farm yet — plant something to start one.');

    body.replaceChildren(
      el('div', 'space-y-3', {},
        el('div', LABEL, {}, 'Account'),
        el('div', `${CARD} grid grid-cols-2 gap-3`, {}, ...rows),
        el('div', LABEL, {}, 'Balances'),
        wallet,
        el('div', LABEL, {}, 'Farm stock'),
        farm));
  }).catch(() => body.replaceChildren(errorBox('Could not load your account information.')));
}

// ─── SECURITY / PASSWORD ─────────────────────────────────────────────────────

export function renderSecurity(body: HTMLElement) {
  const current = el('input', INPUT, { type: 'password', autocomplete: 'current-password', placeholder: 'Current password' }) as HTMLInputElement;
  const next = el('input', INPUT, { type: 'password', autocomplete: 'new-password', placeholder: 'New password (min 8 characters)' }) as HTMLInputElement;
  const confirm = el('input', INPUT, { type: 'password', autocomplete: 'new-password', placeholder: 'Repeat the new password' }) as HTMLInputElement;
  const msg = el('div', 'text-[12px] min-h-[16px]');
  const submit = el('button', BTN_GOLD, {}, 'Change password') as HTMLButtonElement;

  const say = (text: string, ok = false) => {
    msg.textContent = text;
    msg.className = `text-[12px] min-h-[16px] ${ok ? 'text-leaf' : 'text-error'}`;
  };

  submit.addEventListener('click', async () => {
    say('');
    if (!current.value) return say('Enter your current password.');
    if (next.value.length < 8) return say('The new password needs at least 8 characters.');
    if (next.value !== confirm.value) return say('The two new passwords do not match.');
    if (next.value === current.value) return say('The new password must differ from the current one.');
    submit.disabled = true;
    submit.textContent = 'Changing…';
    try {
      await accountApi.changePassword(current.value, next.value);
      current.value = next.value = confirm.value = '';
      say('Password changed. Other devices have been signed out.', true);
      toast('Password changed');
    } catch (e) {
      say((e as { message?: string }).message || 'Could not change the password.');
    } finally {
      submit.disabled = false;
      submit.textContent = 'Change password';
    }
  });
  for (const inp of [current, next, confirm]) {
    inp.addEventListener('keydown', (e) => {
      e.stopPropagation();              // WASD in a password field must not walk the hero
      if ((e as KeyboardEvent).key === 'Enter') submit.click();
    });
  }

  body.replaceChildren(
    el('div', 'space-y-3', {},
      el('div', CARD, {},
        el('div', 'text-sm mb-2', {}, 'Change password'),
        el('div', 'text-[11px] text-text-dim mb-3', {},
          'Changing your password signs out every other device. This one stays signed in.'),
        el('div', 'space-y-2', {}, current, next, confirm),
        el('div', 'flex items-center gap-3 mt-3', {}, submit, msg))));
}

// ─── TRANSACTIONS ────────────────────────────────────────────────────────────

export function renderTransactions(body: HTMLElement) {
  let offset = 0;
  let currency: 'SOFT' | 'PREMIUM' | undefined;

  const list = el('div', 'space-y-2');
  const status = el('div', 'text-[11px] text-text-dim');
  const prev = el('button', BTN, {}, '← Newer') as HTMLButtonElement;
  const nextBtn = el('button', BTN, {}, 'Older →') as HTMLButtonElement;

  const filterBtn = (label: string, value: 'SOFT' | 'PREMIUM' | undefined) => {
    const b = el('button', '', { onClick: () => { currency = value; offset = 0; paint(); void load(); } }, label) as HTMLButtonElement;
    b.dataset.value = value ?? 'ALL';
    return b;
  };
  const filters = [filterBtn('All', undefined), filterBtn('Coins', 'SOFT'), filterBtn('Shrooms', 'PREMIUM')];
  const paint = () => {
    for (const b of filters) {
      const on = (b.dataset.value === 'ALL' ? undefined : b.dataset.value) === currency;
      b.className = `px-2.5 py-1 rounded text-[12px] border transition ${on ? 'border-gold text-gold' : 'border-border text-text-dim hover:text-text'}`;
    }
  };
  paint();

  // type=button and an explicit preventDefault: this button must never carry a
  // default action of its own, whatever it ends up nested inside later.
  const statementBtn = el('button', BTN, {
    type: 'button',
    onClick: async (e: MouseEvent) => {
      e.preventDefault();
      statementBtn.disabled = true;
      const label = statementBtn.textContent;
      statementBtn.textContent = 'Building…';
      try {
        const blob = await accountApi.statementPdf();
        saveBlob(blob, `terraboma-statement-${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (err) {
        toast(await pdfErrorMessage(err, 'Could not build the statement'), 'error');
      } finally {
        statementBtn.disabled = false;
        statementBtn.textContent = label;
      }
    },
  }, 'Statement PDF') as HTMLButtonElement;

  async function load() {
    list.replaceChildren(spinner());
    try {
      const page: TransactionPage = await accountApi.transactions({ limit: PAGE, offset, currency });
      if (!page.items.length) {
        list.replaceChildren(el('div', 'py-8 text-center text-sm text-text-dim', {},
          offset ? 'No more entries.' : 'No transactions yet — sell a harvest and it will show up here.'));
      } else {
        list.replaceChildren(...page.items.map(txRow));
      }
      const from = page.total ? offset + 1 : 0;
      status.textContent = `${from}–${offset + page.items.length} of ${page.total}`;
      prev.disabled = offset === 0;
      nextBtn.disabled = !page.hasMore;
    } catch {
      list.replaceChildren(errorBox('Could not load your transactions.'));
      prev.disabled = nextBtn.disabled = true;
    }
  }

  prev.addEventListener('click', () => { offset = Math.max(0, offset - PAGE); void load(); });
  nextBtn.addEventListener('click', () => { offset += PAGE; void load(); });

  body.replaceChildren(
    el('div', 'space-y-3', {},
      el('div', 'flex flex-wrap items-center gap-2', {}, ...filters, el('div', 'flex-1 min-w-[8px]'), statementBtn),
      list,
      el('div', 'flex items-center justify-between gap-2 pt-1', {}, prev, status, nextBtn)));
  void load();
}

function txRow(t: TransactionEntry): HTMLElement {
  const credit = t.amount >= 0;
  // The PDF button ONLY exists on rows backed by a real-money purchase. Invoices
  // are issued for Shroom purchases only — the backend refuses in-game rows by
  // design (account.controller), so on a Coin/in-game row the button was a dead
  // end that could only produce an error toast. The server answers the question
  // for us with `hasInvoice`; the on-screen receipt still covers every row.
  const invoiceBtn = t.hasInvoice
    ? el('button',
      'shrink-0 px-2 py-1 rounded border border-border text-[11px] text-text-dim hover:border-gold hover:text-gold transition disabled:opacity-50',
      { type: 'button', title: 'Download the PDF invoice for this purchase' }, 'PDF') as HTMLButtonElement
    : null;
  invoiceBtn?.addEventListener('click', async (e) => {
    e.preventDefault();
    invoiceBtn.disabled = true;
    try {
      const blob = await accountApi.invoicePdf(t.id);
      saveBlob(blob, `terraboma-receipt-${t.id.slice(-10)}.pdf`);
    } catch (err) {
      toast(await pdfErrorMessage(err, 'Could not build the receipt'), 'error');
    } finally { invoiceBtn.disabled = false; }
  });

  return el('div', `${CARD} flex items-center gap-3`, {},
    el('div', 'min-w-0 flex-1', {},
      el('div', 'text-sm truncate', {}, humanDescription(t.description)),
      el('div', 'text-[11px] text-text-dim', {},
        `${fmtDateTime(t.createdAt)} · ${humanType(t.entryType)}`)),
    el('div', 'text-right shrink-0', {},
      el('div', `text-sm font-mono ${credit ? 'text-leaf' : 'text-error'}`, {}, `${signed(t.amount)} ${t.currencyLabel}`),
      el('div', 'text-[11px] text-text-dim font-mono', {}, `→ ${t.balanceAfter.toLocaleString()}`)),
    invoiceBtn);
}

/**
 * Name, surname, phone and billing address — optional, editable, clearable.
 *
 * The backend has carried these fields for a while with no way to enter them, so
 * a player could not give us their name even where it matters (an invoice for a
 * real-money purchase is billed to it).
 *
 * The PERSONAL CODE is deliberately not here (owner's call). The column still
 * exists but nothing collects it, which is the right default for a national ID
 * number: identity data you do not ask for is identity data you cannot leak.
 *
 * Everything is optional, so nothing is validated into a corner here — the server
 * owns the rules, and emptying a box clears the field rather than being rejected.
 */
function personalDetailsCard(p: AccountProfile): HTMLElement {
  const INPUT = 'w-full min-h-[44px] px-3 py-2 rounded-lg bg-surface border border-border '
    + 'text-sm text-text placeholder:text-text-dim/70 focus:outline-none focus:border-gold';

  const input = (name: string, label: string, value: string | null, placeholder: string, extra = {}) => {
    const el_ = el('input', INPUT, {
      value: value ?? '', placeholder, name, autocomplete: name, ...extra,
    }) as HTMLInputElement;
    return { el: el_, wrap: el('label', 'block', {},
      el('span', 'block text-[11px] text-text-dim mb-1', {}, label), el_) };
  };

  const first = input('given-name', 'First name', p.personal?.firstName ?? null, 'Optional');
  const last = input('family-name', 'Surname', p.personal?.lastName ?? null, 'Optional');
  const phone = input('tel', 'Phone', p.personal?.phone ?? null, 'Optional', { inputmode: 'tel', type: 'tel' });
  const street = input('street-address', 'Street address', p.personal?.address ?? null, 'Optional');
  const city = input('address-level2', 'City', p.personal?.city ?? null, 'Optional');
  const zip = input('postal-code', 'Postal code', p.personal?.zipCode ?? null, 'Optional');

  const countrySelect = el('select', INPUT, {
    name: 'country', autocomplete: 'country',
  }) as HTMLSelectElement;
  countrySelect.append(el('option', '', { value: '' }, 'Select country…'));
  for (const c of COUNTRIES) countrySelect.append(el('option', '', { value: c.code }, c.name));
  countrySelect.value = p.personal?.country ?? '';
  const countryWrap = el('label', 'block', {},
    el('span', 'block text-[11px] text-text-dim mb-1', {}, 'Country'), countrySelect);

  const status = el('span', 'text-[11px] text-text-dim');
  const save = el('button',
    'min-h-[44px] px-4 rounded-lg border border-gold text-gold text-sm hover:bg-gold/10 disabled:opacity-50',
    {}, 'Save') as HTMLButtonElement;

  save.addEventListener('click', async () => {
    save.disabled = true;
    status.textContent = 'Saving…';
    status.className = 'text-[11px] text-text-dim';
    try {
      // Sent verbatim, empty string included: that is how a field is CLEARED.
      // Trimming to undefined here would make a box you emptied silently keep its
      // old value, which is the most annoying possible outcome.
      const saved = await accountApi.savePersonalDetails({
        firstName: first.el.value.trim(),
        lastName: last.el.value.trim(),
        phone: phone.el.value.trim(),
        address: street.el.value.trim(),
        city: city.el.value.trim(),
        zipCode: zip.el.value.trim(),
        country: countrySelect.value || null,
      });
      first.el.value = saved.firstName ?? '';
      last.el.value = saved.lastName ?? '';
      phone.el.value = saved.phone ?? '';
      street.el.value = saved.address ?? '';
      city.el.value = saved.city ?? '';
      zip.el.value = saved.zipCode ?? '';
      countrySelect.value = saved.country ?? '';
      status.textContent = 'Saved';
      status.className = 'text-[11px] text-leaf';
    } catch (e) {
      // The server's message names the offending field; a generic "failed" would
      // leave the player guessing which box it disliked.
      status.textContent = e instanceof ApiError ? e.message : 'Could not save';
      status.className = 'text-[11px] text-error';
    } finally { save.disabled = false; }
  });

  return el('div', CARD, {},
    el('div', 'text-sm mb-1', {}, 'Your details'),
    el('p', 'text-[11px] text-text-dim mb-3', {},
      'Optional. A name and address here are what an invoice is made out to, instead of your player handle.'),
    el('div', 'grid grid-cols-1 sm:grid-cols-2 gap-3', {}, first.wrap, last.wrap),
    el('div', 'mt-3', {}, phone.wrap),
    el('div', 'mt-3', {}, countryWrap),
    el('div', 'mt-3', {}, street.wrap),
    el('div', 'grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3', {}, city.wrap, zip.wrap),
    el('div', 'flex items-center gap-3 mt-3', {}, save, status));
}
